import { useQuery, useQueries } from '@tanstack/react-query'
import { api } from '../api/client'
import { useTimeZoneStore } from '../store/useTimeZoneStore'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'
import { localHourMinuteLabel, getOffsetMs } from '../utils/timeUtils'

export const GRAF_COLUMNS = [
  { tsId: '10M_TEMP',        label: 'Teplota',          unit: '°C'  },
  { tsId: '10M_TGROUND',     label: 'Teplota přízemní', unit: '°C'  },
  { tsId: '10M_WSMAX',       label: 'Max. vítr',        unit: 'm/s' },
  { tsId: '10M_RAWHUMIDITY', label: 'Vlhkost',          unit: '%'   },
  { tsId: '10M_SUNSHINE',    label: 'Svit',             unit: 's'   },
  { tsId: '10M_WS',          label: 'Vítr',             unit: 'm/s' },
  { tsId: '10M_WD',          label: 'Směr větru',       unit: '°'   },
  { tsId: 'HLY_PRESSSEA',    label: 'Tlak',             unit: 'hPa' },
  { tsId: '10M_PRECIP',      label: 'Srážky 10 min',    unit: 'mm'  },
  { tsId: 'DLY_SNOW',        label: 'Výška sněhu',      unit: 'cm', daily: true },
  { tsId: '10M_SNOWWATER',   label: 'Vodní hodnota',    unit: 'mm'  },
]

function pad2(n) {
  return String(n).padStart(2, '0')
}

function dateToApiStr(year, month, day, hour, minute) {
  return `${year}${pad2(month)}${pad2(day)}${pad2(hour)}${pad2(minute)}`
}

// Vrátí { from, to } ve formátu YYYYMMDDHHMM pro standardní periodu (celé dny)
export function calcFromTo(dateStr, windowDays, alignEnd) {
  const [y, m, d] = dateStr.split('-').map(Number)
  if (alignEnd) {
    const to = dateToApiStr(y, m, d, 23, 59)
    const fromDate = new Date(Date.UTC(y, m - 1, d) - (windowDays - 1) * 86400000)
    const from = dateToApiStr(fromDate.getUTCFullYear(), fromDate.getUTCMonth() + 1, fromDate.getUTCDate(), 0, 0)
    return { from, to }
  } else {
    const from = dateToApiStr(y, m, d, 0, 0)
    const toDate = new Date(Date.UTC(y, m - 1, d) + (windowDays - 1) * 86400000)
    const to = dateToApiStr(toDate.getUTCFullYear(), toDate.getUTCMonth() + 1, toDate.getUTCDate(), 23, 59)
    return { from, to }
  }
}

// Vrátí { from, to } ve formátu YYYYMMDDHHMM pro celý měsíc (1. 00:00 – poslední den 23:59).
// Pozor: konec je vždy celý měsíc, i u aktuálního (rozpracovaného) měsíce — graf má zobrazit
// celou osu zvoleného měsíce. Omezení "jen do včerejška" se týká jen STAHOVÁNÍ dat
// (/daily_ts end_date), ne rozsahu osy grafu.
export function calcMonthFromTo(year, month) {
  const from = dateToApiStr(year, month, 1, 0, 0)
  const lastDay = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const to = dateToApiStr(year, month, lastDay, 23, 59)
  return { from, to }
}

// Vrátí URL pro /chart endpoint
// cfg/gran parametrizovatelné — GrafView (10min) používá výchozí dmulti_10m/10m, HlyGrafView/DlyGrafView
// (viz doc/plans/station-group-meas-chart-gran.md bod 2) volají s cfg='dmulti_hly'/gran='hly' apod.
export function buildChartUrl(baseUrl, station, dateStr, windowDays, alignEnd, isAktual, tz, format = 'png', cfg = 'dmulti_10m', gran = '10m') {
  const type = 'nowmulti'
  const tzParam = encodeURIComponent(tz)
  // format = null/'' → nepřidávej &format (volající si ho doplní, viz ChartFrame withJsonFormat)
  const fmtPart = format ? `&format=${encodeURIComponent(format)}` : ''
  if (isAktual) {
    const minutes = windowDays * 1440
    // 10m: server spočítá to = floor((now − 5 min) / 10 min) * 10 min — OK.
    // hly: totéž se stepMs = 60 min by prvních 30 min každé hodiny "schovalo" už dostupnou
    //      předchozí hodinu (server: to = floor((now − 30 min)/60 min)*60 min). Proto pošleme
    //      přímo from/to s koncem na poslední dokončené hodině, ať to sedí s tabulkou.
    if (gran === 'hly') {
      const stepMs = 60 * 60_000
      const toMs = Math.floor(Date.now() / stepMs) * stepMs
      const fromMs = toMs - windowDays * 1440 * 60_000
      // from/to posíláme jako lokální čas v uživatelské TZ (+ &timezone) — server přes toUtc()
      // vrátí zpět správnou UTC hranici a osa grafu zůstane v uživatelské TZ.
      const fmtLocal = ms => {
        const l = new Date(ms + getOffsetMs(new Date(ms), tz))
        return `${l.getUTCFullYear()}${pad2(l.getUTCMonth() + 1)}${pad2(l.getUTCDate())}${pad2(l.getUTCHours())}${pad2(l.getUTCMinutes())}`
      }
      return `${baseUrl}/chart?type=${type}&cfg=${cfg}&station=${station}&from=${fmtLocal(fromMs)}&to=${fmtLocal(toMs)}&timezone=${tzParam}${fmtPart}&gran=${gran}`
    }
    return `${baseUrl}/chart?type=${type}&cfg=${cfg}&station=${station}&minutes=${minutes},10&timezone=${tzParam}${fmtPart}&gran=${gran}`
  }
  const { from, to } = calcFromTo(dateStr, windowDays, alignEnd)
  return `${baseUrl}/chart?type=${type}&cfg=${cfg}&station=${station}&from=${from}&to=${to}&timezone=${tzParam}${fmtPart}&gran=${gran}`
}

// Vrátí begin_date/end_date pro /daily_ts pokrývající celý rozsah okna, TZ-aware
export function calcTableRange(dateStr, windowDays, alignEnd, isAktual, tz) {
  if (isAktual) {
    // Stejná logika jako Java ChartHandler: to = aktuální čas zaokrouhlený dolů na 10min krok
    const stepMs = 10 * 60_000
    const nowMs = Date.now()
    const toMs = Math.floor((nowMs - stepMs / 2) / stepMs) * stepMs
    const fromMs = toMs - windowDays * 1440 * 60_000
    return {
      begin_date: new Date(fromMs).toISOString().slice(0, 19) + 'Z',
      end_date:   new Date(toMs).toISOString().slice(0, 19) + 'Z',
    }
  }

  // Vypočítá UTC čas odpovídající lokální půlnoci (00:00) daného dne v zadané TZ
  function localMidnightUtc(y, m, d) {
    const nominalUtcMs = Date.UTC(y, m - 1, d, 0, 0, 0)
    const offset = getOffsetMs(new Date(nominalUtcMs), tz)
    return nominalUtcMs - offset
  }

  // Vypočítá UTC čas odpovídající lokálnímu 23:59 daného dne v zadané TZ
  function localEndOfDayUtc(y, m, d) {
    const nominalUtcMs = Date.UTC(y, m - 1, d, 23, 59, 0)
    const offset = getOffsetMs(new Date(nominalUtcMs), tz)
    return nominalUtcMs - offset
  }

  function toIsoZ(ms) {
    return new Date(ms).toISOString().slice(0, 19) + 'Z'
  }

  const [y, m, d] = dateStr.split('-').map(Number)
  if (alignEnd) {
    const fromDateMs = Date.UTC(y, m - 1, d) - (windowDays - 1) * 86400000
    const fromDate = new Date(fromDateMs)
    return {
      begin_date: toIsoZ(localMidnightUtc(fromDate.getUTCFullYear(), fromDate.getUTCMonth() + 1, fromDate.getUTCDate())),
      end_date:   toIsoZ(localEndOfDayUtc(y, m, d)),
    }
  } else {
    const toDateMs = Date.UTC(y, m - 1, d) + (windowDays - 1) * 86400000
    const toDate = new Date(toDateMs)
    return {
      begin_date: toIsoZ(localMidnightUtc(y, m, d)),
      end_date:   toIsoZ(localEndOfDayUtc(toDate.getUTCFullYear(), toDate.getUTCMonth() + 1, toDate.getUTCDate())),
    }
  }
}

// Vrátí begin_date/end_date pro /daily_ts pokrývající celý rozsah okna hodinových dat, TZ-aware
// (obdoba calcTableRange, ale konec dne je HH:00, ne HH:59 — viz useHlyOneDayData)
export function calcHlyTableRange(dateStr, windowDays, alignEnd, isAktual, tz) {
  if (isAktual) {
    // Konec okna = poslední dokončená hodina (bez −stepMs/2 zaokrouhlení, které by u hodinového
    // kroku prvních 30 min každé hodiny "schovalo" už dostupnou předchozí hodinu).
    const stepMs = 60 * 60_000
    const nowMs = Date.now()
    const toMs = Math.floor(nowMs / stepMs) * stepMs
    const fromMs = toMs - windowDays * 24 * 60 * 60_000
    return {
      begin_date: new Date(fromMs).toISOString().slice(0, 19) + 'Z',
      end_date:   new Date(toMs).toISOString().slice(0, 19) + 'Z',
    }
  }

  function localMidnightUtc(y, m, d) {
    const nominalUtcMs = Date.UTC(y, m - 1, d, 0, 0, 0)
    const offset = getOffsetMs(new Date(nominalUtcMs), tz)
    return nominalUtcMs - offset
  }

  function localEndOfDayUtc(y, m, d) {
    const nominalUtcMs = Date.UTC(y, m - 1, d, 23, 0, 0)
    const offset = getOffsetMs(new Date(nominalUtcMs), tz)
    return nominalUtcMs - offset
  }

  function toIsoZ(ms) {
    return new Date(ms).toISOString().slice(0, 19) + 'Z'
  }

  const [y, m, d] = dateStr.split('-').map(Number)
  if (alignEnd) {
    const fromDateMs = Date.UTC(y, m - 1, d) - (windowDays - 1) * 86400000
    const fromDate = new Date(fromDateMs)
    return {
      begin_date: toIsoZ(localMidnightUtc(fromDate.getUTCFullYear(), fromDate.getUTCMonth() + 1, fromDate.getUTCDate())),
      end_date:   toIsoZ(localEndOfDayUtc(y, m, d)),
    }
  } else {
    const toDateMs = Date.UTC(y, m - 1, d) + (windowDays - 1) * 86400000
    const toDate = new Date(toDateMs)
    return {
      begin_date: toIsoZ(localMidnightUtc(y, m, d)),
      end_date:   toIsoZ(localEndOfDayUtc(toDate.getUTCFullYear(), toDate.getUTCMonth() + 1, toDate.getUTCDate())),
    }
  }
}

export function useGrafTableData(ghId, dateStr, windowDays, alignEnd, isAktual, enabled = true) {
  const tz = useTimeZoneStore(s => s.tz)
  const tsIds = GRAF_COLUMNS.map(c => c.tsId)
  const { begin_date, end_date } = calcTableRange(dateStr, windowDays, alignEnd, isAktual, tz)

  const queryKey = ['grafData', ghId, dateStr, windowDays, alignEnd, isAktual, tz]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      console.debug('[GrafData] Načítám daily_ts:', { ghId, begin_date, end_date, tsIds })
      const responses = await fetchTsIds(
        tsIds,
        tsId => api.get(`/daily_ts?ts_id=${tsId}&gh_id=${ghId}&begin_date=${begin_date}&end_date=${end_date}`),
      )
      const result = Object.fromEntries(tsIds.map((tsId, i) => [tsId, responses[i]?.data ?? []]))
      const toFill = responses.flatMap(r => r?.to_fill ?? [])
      const counts = Object.fromEntries(tsIds.map(tsId => [tsId, result[tsId]?.length ?? 0]))
      console.debug('[GrafData] daily_ts načteno:', counts, 'to_fill:', toFill.length)
      return { result, toFill }
    },
    enabled: !!ghId && enabled,
    staleTime: 0,
  })

  // Dotažení chybějících řad: resp.data je kompletní řada → nahradit (ne konkatenovat).
  useToFill(queryKey, data, (result, resp, item) => {
    const tsId = item.params?.ts_id
    if (!tsId || !(tsId in result)) return result
    return { ...result, [tsId]: resp.data }
  }, { gran: '10M', live: isAktual })

  const dataMap = new Map()
  const labelMap = new Map()
  const dayMap = new Map()

  const beginMs = new Date(begin_date).getTime()

  if (data?.result) {
    GRAF_COLUMNS.forEach(col => {
      ;(data.result[col.tsId] ?? []).forEach(row => {
        if (row.dt == null) return
        const d = new Date(row.dt)
        if (d.getTime() < beginMs) return
        const key = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())} ${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`
        const entry = dataMap.get(key) ?? {}
        entry[col.tsId] = row.value ?? null
        dataMap.set(key, entry)
        if (!labelMap.has(key)) {
          labelMap.set(key, localHourMinuteLabel(d, tz))
          const local = new Date(d.getTime() + getOffsetMs(d, tz))
          dayMap.set(key, {
            utcDay: `${d.getUTCDate()}.${d.getUTCMonth() + 1}.`,
            localDay: `${local.getUTCDate()}.${local.getUTCMonth() + 1}.`,
            dateStr: `${local.getUTCFullYear()}-${pad2(local.getUTCMonth() + 1)}-${pad2(local.getUTCDate())}`,
          })
        }
      })
    })
  }

  // Odstraň řádky kde všechny hodnoty jsou null
  for (const key of dataMap.keys()) {
    const entry = dataMap.get(key)
    if (Object.values(entry).every(v => v == null)) {
      dataMap.delete(key)
      labelMap.delete(key)
      dayMap.delete(key)
    }
  }

  return { dataMap, labelMap, dayMap, isLoading, isError }
}
